import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import Modal from "@/components/Modal";
import SkeletonRow from "@/components/SkeletonRow"; // Import SkeletonRow
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import useDataPendaftaran from "@/hooks/useDataPendaftaran";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import {
  getDataSekolahAsal,
  getDataStep,
  getListJurusanSekolah,
  getListMapelKumer,
  getListSekolah,
} from "@/services/getData";
import {
  DataMapelPeminatan,
  DataSiswa,
  JurusanSekolah,
  MapelPeminatanKumer,
  Option,
  Sekolah,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  GraduationCap,
  Info,
  Loader2,
  School,
  Search,
} from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

type InputState = {
  id_ref_sekolah: string;
  is_kurikulum_merdeka: boolean | null | number;
  id_ref_jurusan_sekolah: string;
};

const konfirmasiDataSekolah = async (form: InputState) => {
  const response = await api.patch(
    "/siswa/penerimaan/smbjm/pilihan-prodi/data-sekolah-asal/konfirmasi",
    form,
    { withCredentials: true },
  );
  return response.data;
};

export default function DataSekolah() {
  const { user } = useUser();
  const { timePassedActive } = useTimePeriode();
  const { isValidPendaftaran } = useDataPendaftaran();
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const [searchQuerySekolah, setSearchQuerySekolah] = useState("");
  const [openListSekolah, setOpenListSekolah] = useState(false);
  const [openListJurusan, setOpenListJurusan] = useState(false);
  const [selectedMapels, setSelectedMapels] = useState<string[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const [form, setForm] = useState<InputState>({
    id_ref_sekolah: "",
    is_kurikulum_merdeka: null,
    id_ref_jurusan_sekolah: "",
  });
  const [isConfirm, setIsConfirm] = useState(false);

  // --- QUERIES ---
  const { data: dataSekolahAsal, isPending: isPendingDataSekolahAsal } =
    useQuery<DataSiswa>({
      queryKey: ["dataSekolahAsal", user?.id],
      queryFn: () => getDataSekolahAsal(),
      enabled: !!user?.id,
    });

  const {
    data: dataStep,
    isFetched: isFetchedDataStep,
    isPending: isPendingDataStep,
  } = useQuery({
    queryKey: ["dataStep", sessionPeriode?.id_sys_periode],
    queryFn: () => getDataStep(sessionPeriode?.id_sys_periode || ""),
    enabled: !!sessionPeriode?.id_sys_periode,
  });

  const { data: dataSekolah, isPending: isPendingSekolah } = useQuery({
    queryKey: ["listSekolah", searchQuerySekolah, form.id_ref_sekolah],
    queryFn: () =>
      getListSekolah(
        "1",
        "10",
        searchQuerySekolah ? searchQuerySekolah : form.id_ref_sekolah,
      ),
    select: (data) =>
      data?.data?.map((sekolah: Sekolah) => ({
        label: `${sekolah.nama_sekolah} (${sekolah.npsn_sekolah})`,
        value: `${sekolah.id_ref_sekolah}`,
        bentuk_pendidikan: sekolah.bentuk_pendidikan,
      })),
  });

  const [selectedSekolah, setSelectedSekolah] = useState<
    (Option & { bentuk_pendidikan: string }) | null
  >(null);

  const { data: listJurusan, isPending: isPendingListJurusan } = useQuery({
    queryKey: ["listJurusan", selectedSekolah?.bentuk_pendidikan],
    queryFn: () => getListJurusanSekolah(selectedSekolah?.bentuk_pendidikan),
    select: (data) =>
      data?.map((jurusan: JurusanSekolah) => ({
        label: jurusan.nama_jurusan_sekolah,
        value: `${jurusan.id_ref_jurusan_sekolah}`,
      })),
    enabled: !!selectedSekolah,
  });

  const filteredListJurusan = listJurusan?.filter((option: Option) => {
    return !option?.label?.startsWith("Umum");
  });

  const { data: listMapelPeminatan } = useQuery({
    queryKey: ["listMapelPeminatan"],
    queryFn: () => getListMapelKumer(),
    select: (data) =>
      data?.map((mapel: MapelPeminatanKumer) => ({
        label: mapel.nama_mapel,
        value: `${mapel?.idref_mapel_peminatan_kurikulum_merdeka}`,
      })),
  });

  // --- EFFECTS ---
  useEffect(() => {
    if (dataSekolahAsal) {
      setSearchQuerySekolah(dataSekolahAsal?.sekolah?.npsn_sekolah || "");
      setForm((prev) => ({
        ...prev,
        id_ref_sekolah: prev.id_ref_sekolah
          ? prev.id_ref_sekolah
          : dataSekolahAsal.id_ref_sekolah || "",
        is_kurikulum_merdeka:
          prev.is_kurikulum_merdeka !== null
            ? prev.is_kurikulum_merdeka
            : dataSekolahAsal.is_kurikulum_merdeka === 0
              ? false
              : dataSekolahAsal.is_kurikulum_merdeka === 1
                ? true
                : null,
        id_ref_jurusan_sekolah: prev.id_ref_jurusan_sekolah
          ? prev.id_ref_jurusan_sekolah
          : dataSekolahAsal?.id_ref_jurusan_sekolah || "",
      }));
      const listIdMapelPeminatan = dataSekolahAsal?.data_mapel_peminatan?.map(
        (dataMapelPeminatan: DataMapelPeminatan) => {
          return `${dataMapelPeminatan.idref_mapel_peminatan_kurikulum_merdeka}`;
        },
      );
      setSelectedMapels(listIdMapelPeminatan as string[]);
    }
  }, [dataSekolahAsal]);

  useEffect(() => {
    const currentSekolah = dataSekolah?.find((option: Option) => {
      return option.value == form.id_ref_sekolah;
    });
    setSelectedSekolah(currentSekolah);
  }, [dataSekolah]);

  // --- HELPERS ---
  const isSMA =
    selectedSekolah &&
    (selectedSekolah?.bentuk_pendidikan?.startsWith("SMA") ||
      selectedSekolah?.bentuk_pendidikan?.startsWith("MA"));
  const isSMK =
    selectedSekolah && selectedSekolah?.bentuk_pendidikan?.startsWith("SMK");
  const currentMapels = selectedMapels?.length
    ? (listMapelPeminatan?.filter((option: Option) =>
        selectedMapels.includes(option.value),
      ) ?? [])
    : [];
  const currentJurusan = listJurusan?.find((option: Option) => {
    return option.value == form.id_ref_jurusan_sekolah;
  });

  // --- MUTATION ---
  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingKonfirmasi,
    error,
  } = useMutation({
    mutationFn: konfirmasiDataSekolah,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["dataSiswa"] });
      queryClient.invalidateQueries({ queryKey: ["dataSekolahAsal"] });
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      queryClient.invalidateQueries({ queryKey: ["listProdi"] });
      toast.success(data?.message || "Data sekolah berhasil dikonfirmasi.");
      setShowConfirm(false);
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "Terjadi kesalahan saat menyimpan data.";
      toast.error(errorMessage);
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.id_ref_sekolah) return toast.error("Sekolah belum dipilih!");
    if (
      (isSMK && !form.id_ref_jurusan_sekolah) ||
      (isSMA && !form.is_kurikulum_merdeka && !form.id_ref_jurusan_sekolah)
    )
      return toast.error("Jurusan sekolah asal belum dipilih!");
    if (isSMA && form.is_kurikulum_merdeka && selectedMapels?.length == 0)
      return toast.error("Mata pelajaran peminatan belum dipilih");
    setShowConfirm(true);
    setIsConfirm(false);
  };

  const handleConfirm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = {
      ...form,
      id_sys_periode: sessionPeriode?.id_sys_periode,
      list_id_ref_mapel_peminatan_kurikulum_merdeka: selectedMapels,
      id_ref_jurusan_sekolah: form.id_ref_jurusan_sekolah,
    };
    mutate(data);
  };

  // --- SKELETON LOADING ---
  if (isPendingDataSekolahAsal || isPendingSekolah || isPendingDataStep) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <SkeletonRow className="h-8 w-8 rounded-full" />
          <SkeletonRow className="h-8 w-48 rounded-lg" />
        </div>
        <SkeletonRow className="h-20 w-full rounded-xl" />
        <div className="border rounded-xl p-6 space-y-4">
          <SkeletonRow className="h-6 w-32 rounded-lg mb-2" />
          <SkeletonRow className="h-10 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500 grid grid-cols-1">
      {/* HEADER & ALERT */}
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <School className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          Data Sekolah Asal
        </h1>

        <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 text-amber-900 dark:text-amber-200">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <AlertTitle className="font-bold text-amber-800 dark:text-amber-300">
            Penting!
          </AlertTitle>
          <AlertDescription className="text-sm mt-1">
            Konfirmasi data sekolah asal dan kurikulum sangat penting untuk
            penentuan program studi. <br />
            <strong>
              Data yang sudah dikonfirmasi tidak dapat diubah kembali.
            </strong>
          </AlertDescription>
        </Alert>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* CARD 1: PILIH SEKOLAH */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100 dark:border-slate-800">
            <Search className="w-5 h-5 text-slate-500" />
            <h3 className="font-semibold text-lg text-slate-800 dark:text-slate-200">
              Cari Sekolah
            </h3>
          </div>

          <div className="space-y-2 grid grid-cols-1 overflow-auto">
            <Label required>Nama Sekolah / NPSN</Label>
            <Combobox
              serverSide
              open={openListSekolah}
              setOpen={setOpenListSekolah}
              value={form.id_ref_sekolah || ""}
              setValue={(newValue) => {
                const selected = dataSekolah?.find(
                  (option: Option) => String(option.value) == String(newValue),
                );
                setSelectedSekolah(selected);
                setForm((prev) => ({
                  ...prev,
                  id_ref_sekolah: newValue,
                  id_ref_jurusan_sekolah: "",
                }));
              }}
              onChange={(event) => setSearchQuerySekolah(event.target.value)}
              options={dataSekolah}
              placeholder="Ketik nama sekolah atau NPSN..."
              isPending={isPendingSekolah}
              searchQueryValue={searchQuerySekolah || ""}
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-1">
              <Info className="w-3 h-3" /> Jika sekolah tidak muncul, coba cari
              menggunakan <strong>NPSN</strong>.
            </p>
            <InputError
              text={error?.response?.data.errors?.id_ref_sekolah?.join(",")}
            />
          </div>
        </div>

        {/* CARD 2: KURIKULUM & JURUSAN (Show if school selected) */}
        {form.id_ref_sekolah && (
          <div className="grid-cols-1 overflow-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm animate-in slide-in-from-bottom-2 ">
            <div className="flex items-center gap-2 mb-6 pb-2 border-b border-slate-100 dark:border-slate-800">
              <BookOpen className="w-5 h-5 text-slate-500" />
              <h3 className="font-semibold text-lg text-slate-800 dark:text-slate-200">
                Detail Pendidikan
              </h3>
            </div>

            <div className="space-y-6">
              {/* Kurikulum Selection */}
              <div className="space-y-3">
                <Label required>Kurikulum yang digunakan</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {/* Option: K-13 */}
                  <div
                    className={`cursor-pointer border rounded-lg p-4 flex items-center gap-3 transition-all ${
                      form.is_kurikulum_merdeka === false
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500"
                        : "border-slate-200 dark:border-slate-700 hover:border-blue-300"
                    }`}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        is_kurikulum_merdeka: false,
                      }))
                    }
                  >
                    <div
                      className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                        form.is_kurikulum_merdeka === false
                          ? "border-blue-600"
                          : "border-slate-400"
                      }`}
                    >
                      {form.is_kurikulum_merdeka === false && (
                        <div className="w-2 h-2 rounded-full bg-blue-600" />
                      )}
                    </div>
                    <span className="text-sm font-medium">Kurikulum 2013</span>
                  </div>

                  {/* Option: Merdeka */}
                  <div
                    className={`cursor-pointer border rounded-lg p-4 flex items-center gap-3 transition-all ${
                      form.is_kurikulum_merdeka === true
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500"
                        : "border-slate-200 dark:border-slate-700 hover:border-blue-300"
                    }`}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        is_kurikulum_merdeka: true,
                      }))
                    }
                  >
                    <div
                      className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                        form.is_kurikulum_merdeka === true
                          ? "border-blue-600"
                          : "border-slate-400"
                      }`}
                    >
                      {form.is_kurikulum_merdeka === true && (
                        <div className="w-2 h-2 rounded-full bg-blue-600" />
                      )}
                    </div>
                    <span className="text-sm font-medium">
                      Kurikulum Merdeka
                    </span>
                  </div>

                  {/* Option: Lainnya (Non-SMK) */}
                  {!isSMK && (
                    <div
                      className={`cursor-pointer border rounded-lg p-4 flex items-center gap-3 transition-all ${
                        form.is_kurikulum_merdeka === 2
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500"
                          : "border-slate-200 dark:border-slate-700 hover:border-blue-300"
                      }`}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          is_kurikulum_merdeka: 2,
                        }))
                      }
                    >
                      <div
                        className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                          form.is_kurikulum_merdeka === 2
                            ? "border-blue-600"
                            : "border-slate-400"
                        }`}
                      >
                        {form.is_kurikulum_merdeka === 2 && (
                          <div className="w-2 h-2 rounded-full bg-blue-600" />
                        )}
                      </div>
                      <span className="text-sm font-medium">Lainnya</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Jurusan Selection (K-13 or SMK) */}
              {form.is_kurikulum_merdeka !== null &&
                (isSMK ||
                  (isSMA && form.is_kurikulum_merdeka == false) ||
                  (!isSMA && !isSMK && form.is_kurikulum_merdeka == false)) && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                    <Label required>Jurusan / Program Keahlian</Label>
                    <Combobox
                      isPending={isPendingListJurusan}
                      open={openListJurusan}
                      setOpen={setOpenListJurusan}
                      value={form.id_ref_jurusan_sekolah || ""}
                      setValue={(value) => {
                        setForm((prev) => ({
                          ...prev,
                          id_ref_jurusan_sekolah: value,
                        }));
                        setSelectedMapels([]);
                      }}
                      options={filteredListJurusan}
                      placeholder="Pilih jurusan sekolah"
                    />
                    <InputError
                      text={error?.response?.data.errors?.id_ref_jurusan_sekolah?.join(
                        ",",
                      )}
                    />
                  </div>
                )}

              {/* Mapel Peminatan (Kurikulum Merdeka) */}
              {((isSMA && form.is_kurikulum_merdeka) ||
                (!isSMA && !isSMK && form.is_kurikulum_merdeka) ||
                (!isSMA && !isSMK && form.is_kurikulum_merdeka === 2)) && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                  <Label required>
                    Mata Pelajaran Peminatan (Min 4, Max 5)
                  </Label>
                  <MultiSelect
                    options={listMapelPeminatan || []}
                    onValueChange={(value) => {
                      setSelectedMapels(value);
                      setForm((prev) => ({
                        ...prev,
                        id_ref_jurusan_sekolah: "",
                      }));
                    }}
                    defaultValue={selectedMapels}
                    placeholder="Pilih Mapel Peminatan"
                    variant="inverted"
                    animation={2}
                    maxCount={5} // Updated max count visually if needed
                  />
                  <InputError
                    text={error?.response?.data.errors?.list_id_ref_mapel_peminatan_kurikulum_merdeka?.join(
                      ",",
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* BUTTON ACTION */}
        {!isValidPendaftaran &&
          !timePassedActive?.waktu_selesai_daftar_valid_selesai &&
          isFetchedDataStep &&
          !dataStep?.is_confirm_jurusan_sekolah && (
            <div className="flex justify-end pt-4">
              <Button
                size="lg"
                className="w-full sm:w-auto "
                disabled={isPendingKonfirmasi}
              >
                {isPendingKonfirmasi ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />{" "}
                    Memproses...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Konfirmasi Data
                    Sekolah
                  </>
                )}
              </Button>
            </div>
          )}
      </form>

      {/* --- MODAL CONFIRMATION --- */}
      <Modal
        show={showConfirm}
        onClose={() => {
          setShowConfirm(false);
          setIsConfirm(false);
        }}
        className="lg:w-[40rem] md:w-[35rem] w-[95vw]"
      >
        <form onSubmit={handleConfirm} className="p-6">
          <div className="text-center mb-6">
            <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mb-4">
              <GraduationCap className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Konfirmasi Data Sekolah
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
              Mohon periksa kembali data berikut sebelum melanjutkan.
            </p>
          </div>

          <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-4 space-y-3 mb-6 text-sm">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center py-2 border-b border-slate-200 dark:border-slate-800">
              <span className="text-slate-500">Sekolah Asal</span>
              <span className="font-semibold text-right text-slate-800 dark:text-slate-200">
                {selectedSekolah?.label}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row justify-between sm:items-center py-2 border-b border-slate-200 dark:border-slate-800">
              <span className="text-slate-500">Kurikulum</span>
              <span className="font-semibold text-right text-slate-800 dark:text-slate-200">
                {form.is_kurikulum_merdeka == 0
                  ? "Kurikulum 2013"
                  : form.is_kurikulum_merdeka == 1
                    ? "Kurikulum Merdeka"
                    : "Lainnya"}
              </span>
            </div>

            {/* Jika Mapel Peminatan */}
            {form.is_kurikulum_merdeka && (isSMA || (!isSMA && !isSMK)) && (
              <div className="py-2">
                <span className="text-slate-500 block mb-1">
                  Mapel Peminatan
                </span>
                <div className="flex flex-wrap gap-1 justify-end">
                  {currentMapels && currentMapels.length > 0 ? (
                    currentMapels.map((opt: Option) => (
                      <Badge
                        key={opt.value}
                        variant="secondary"
                        className="text-xs bg-white border-slate-200"
                      >
                        {opt.label}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-red-500 font-medium">
                      Belum dipilih!
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Jika Jurusan */}
            {(isSMK || !form.is_kurikulum_merdeka) && (
              <div className="flex flex-col sm:flex-row justify-between sm:items-center py-2">
                <span className="text-slate-500">Jurusan</span>
                <span
                  className={`font-semibold text-right ${
                    currentJurusan
                      ? "text-slate-800 dark:text-slate-200"
                      : "text-red-500"
                  }`}
                >
                  {currentJurusan ? currentJurusan.label : "Belum dipilih!"}
                </span>
              </div>
            )}
          </div>

          <div className="mb-6 bg-amber-50 dark:bg-amber-900/10 p-3 rounded-md border border-amber-100 dark:border-amber-800/50">
            <div className="flex gap-3 items-start">
              <Checkbox
                id="confirm-check"
                className="mt-0.5 border-amber-500 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                checked={isConfirm}
                onCheckedChange={(c) => setIsConfirm(!!c)}
              />
              <Label
                htmlFor="confirm-check"
                className="text-xs text-amber-800 dark:text-amber-200 cursor-pointer leading-relaxed"
              >
                Saya menyatakan bahwa data sekolah dan kurikulum yang saya pilih
                sudah benar. Saya paham data ini{" "}
                <strong>tidak dapat diubah</strong> setelah dikonfirmasi.
              </Label>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                setShowConfirm(false);
                setIsConfirm(false);
              }}
            >
              Batal
            </Button>
            <Button
              type="submit"
              className="w-full"
              disabled={isPendingKonfirmasi || !isConfirm}
            >
              {isPendingKonfirmasi ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Ya, Konfirmasi"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
